import { useEffect, useState } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { API_BASE } from '@/lib/api'
import {
  SSEClient,
  type SSEConnectionStatus,
  type SSEEvent,
  type SSEEventType,
} from '@/lib/sse'

// ─── Event → query-key mapping ──────────────────────────────────────────────
//
// P1-1: the SSEClient was fully written but never instantiated, so every
// agent `update_dashboard_widget` broadcast (and every other SSE event) had
// zero consumers — the dashboard only updated on manual refetch. This hook
// instantiates the client and bridges each SSE event to a TanStack Query
// cache invalidation, which causes the relevant hooks (useStats, useActivity,
// useDecisions, etc.) to refetch and the widgets to update in real time.
//
// We invalidate rather than patch cache shapes directly: SSE payloads are
// partial (e.g. a widget delta) and would risk shape drift against the REST
// responses the hooks expect. Invalidation keeps a single source of truth.

/** Query-key prefixes to invalidate for each named event we care about. */
const EVENT_INVALIDATIONS: Partial<Record<SSEEventType, QueryKey[]>> = {
  'decision.created': [['decisions'], ['stats']],
  'decision.updated': [['decisions'], ['stats']],
  'activity.logged': [['activity'], ['stats']],
  'compliance.updated': [['compliance-alerts'], ['stats']],
  'compliance.critical': [['compliance-alerts'], ['stats']],
  'finance.mrr_updated': [['stats']],
  'finance.widget_update': [['stats']],
  'finance.payment_failed': [['stats']],
  'market.scan.complete': [['stats'], ['agents']],
  'market.intel.updated': [['stats'], ['agents']],
  // Decision-queue overload signals (overload-detector.ts) — surface via the
  // decision queue + dashboard aggregate.
  'system.overload-critical': [['decisions'], ['stats']],
  'system.overload-warning': [['decisions'], ['stats']],
}

/**
 * The generic widget channel. `update_dashboard_widget` broadcasts here with a
 * `widgetId`; we map well-known widget IDs to their dedicated query, and fall
 * back to the dashboard aggregate + agent list for everything else.
 */
const WIDGET_QUERY_BY_ID: Record<string, QueryKey[]> = {
  'integration-health': [['integrations']],
  'system-health': [['health']],
  'customer-health': [['stats']],
  'customer-onboarding': [['stats']],
}

const WIDGET_FALLBACK_KEYS: QueryKey[] = [['stats'], ['agents']]

interface WidgetUpdatePayload {
  widgetId?: string
}

/**
 * Pure resolver: given an inbound SSE event, return the query keys to
 * invalidate. Extracted from the hook so it can be unit-tested without a DOM /
 * EventSource. Returns an empty array for events with no dashboard query
 * (heartbeats, the `connected` frame, briefing progress).
 */
export function resolveSSEInvalidations(event: SSEEvent): QueryKey[] {
  if (event.type === 'widget:update') {
    const { widgetId } = (event.data as WidgetUpdatePayload | null) ?? {}
    return (widgetId && WIDGET_QUERY_BY_ID[widgetId]) || WIDGET_FALLBACK_KEYS
  }
  return EVENT_INVALIDATIONS[event.type] ?? []
}

/**
 * Instantiates a single SSEClient for the lifetime of the dashboard shell and
 * routes inbound events to query-cache invalidations. Returns the live
 * connection status so the shell can show a real-time indicator.
 *
 * Mount this once, high in the authenticated tree (DashboardLayout). It is
 * intentionally NOT called per-page so a single EventSource is shared.
 */
export function useDashboardSSE(): SSEConnectionStatus {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<SSEConnectionStatus>('connecting')

  useEffect(() => {
    const client = new SSEClient(`${API_BASE}/api/v1/stream`)

    const invalidate = (keys: QueryKey[]): void => {
      for (const queryKey of keys) {
        void queryClient.invalidateQueries({ queryKey })
      }
    }

    const offStatus = client.onStatusChange(setStatus)

    const offEvents = client.on('*', (event: SSEEvent) => {
      // Heartbeats (health.tick), the initial `connected` frame, and briefing
      // progress events resolve to [] and are intentionally ignored.
      invalidate(resolveSSEInvalidations(event))
    })

    return () => {
      offStatus()
      offEvents()
      client.close()
    }
  }, [queryClient])

  return status
}
